import jsPDF from "jspdf";

type CertificateFont = {
  fileName: string;
  family: "Montserrat" | "Poppins";
  style: "normal" | "bold" | "italic" | "bolditalic";
};

const CERTIFICATE_FONTS: CertificateFont[] = [
  { fileName: "Montserrat-Regular.ttf", family: "Montserrat", style: "normal" },
  { fileName: "Montserrat-Bold.ttf", family: "Montserrat", style: "bold" },
  { fileName: "Montserrat-Italic.ttf", family: "Montserrat", style: "italic" },
  { fileName: "Montserrat-BoldItalic.ttf", family: "Montserrat", style: "bolditalic" },
  { fileName: "Poppins-Regular.ttf", family: "Poppins", style: "normal" },
  { fileName: "Poppins-Bold.ttf", family: "Poppins", style: "bold" },
  { fileName: "Poppins-Italic.ttf", family: "Poppins", style: "italic" },
  { fileName: "Poppins-BoldItalic.ttf", family: "Poppins", style: "bolditalic" },
];

const fontDataCache = new Map<string, Promise<string>>();
const registeredDocuments = new WeakSet<object>();
let previewFontsPromise: Promise<void> | null = null;

const getFontUrl = (fileName: string) => {
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}/fonts/certificates/${fileName}`;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return window.btoa(binary);
};

const loadFontData = (fileName: string) => {
  const cachedData = fontDataCache.get(fileName);
  if (cachedData) return cachedData;

  const request = fetch(getFontUrl(fileName))
    .then(async (response) => {
      if (!response.ok) throw new Error(`Não foi possível carregar a fonte ${fileName}.`);
      return arrayBufferToBase64(await response.arrayBuffer());
    })
    .catch((error) => {
      fontDataCache.delete(fileName);
      throw error;
    });

  fontDataCache.set(fileName, request);
  return request;
};

export const registerCertificatePdfFonts = async (doc: jsPDF) => {
  if (registeredDocuments.has(doc)) return;

  const fontData = await Promise.all(
    CERTIFICATE_FONTS.map(async (font) => ({ ...font, data: await loadFontData(font.fileName) })),
  );

  fontData.forEach((font) => {
    doc.addFileToVFS(font.fileName, font.data);
    doc.addFont(font.fileName, font.family, font.style);
  });

  registeredDocuments.add(doc);
};

export const loadCertificatePreviewFonts = () => {
  if (previewFontsPromise) return previewFontsPromise;

  previewFontsPromise = Promise.all(
    CERTIFICATE_FONTS.map(async (font) => {
      const fontFace = new FontFace(
        `Certificate ${font.family}`,
        `url(${getFontUrl(font.fileName)}) format("truetype")`,
        {
          style: font.style.includes("italic") ? "italic" : "normal",
          weight: font.style.includes("bold") ? "700" : "400",
        },
      );
      const loadedFont = await fontFace.load();
      document.fonts.add(loadedFont);
    }),
  ).then(() => undefined).catch((error) => {
    previewFontsPromise = null;
    throw error;
  });

  return previewFontsPromise;
};
